import * as React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import Button from '@mui/material/Button';
import { ITruckTypeFormValues, truckTypeFormSchema } from '../../../../@types/model/masterData/truckType/truckTypeFormValues';
import { PillButton, ControlledTextField } from '@zz2/zz2-ui';
import { ControlledLabelledCheckBox } from '../../../../components/input/form/ControlledLabelledCheckBoxWrapper';
import CustomReactHookFormDevTools from '../../../../components/devtools/ReactHookFormDevTools';

interface ITruckTypeFormProps {
    isLoading : boolean;
    initialValues : ITruckTypeFormValues;
    onSubmit : (values : ITruckTypeFormValues) => void;
    onClose : () => void;
}

const TruckTypeForm = (props : ITruckTypeFormProps) : React.ReactElement => {
    const schema = truckTypeFormSchema;

    const { handleSubmit, control, formState: { isDirty, errors } } = useForm<ITruckTypeFormValues>({
        defaultValues: props.initialValues,
        resolver: zodResolver(schema),
        mode: 'onSubmit', // Changed from 'onChange' to 'onSubmit' to trigger validation on submit
    });

    const { initialValues, onSubmit, onClose, isLoading } = props;

    // Custom submit handler to allow submission even with validation errors
    const handleFormSubmit = (data : ITruckTypeFormValues) => {
        // Always call onSubmit, even if there are validation errors
        // The parent component will handle validation and show appropriate messages
        onSubmit(data);
    };

    return (
        <div>
            <CustomReactHookFormDevTools control={control} />
            <form onSubmit={handleSubmit(handleFormSubmit)}>
                <div className={'fdc p10 pt15 pb10'}>
                    <div className={'fdc aic'}>
                        <ControlledTextField
                            className={'mb10 wfill'}
                            name={'code'}
                            label={'Code'}
                            control={control}
                            defaultValue={initialValues.code}
                        />
                        <ControlledTextField
                            className={'mb10 wfill'}
                            name={'description'}
                            label={'Description'}
                            control={control}
                            defaultValue={initialValues.description}
                        />
                        <ControlledTextField
                            className={'mb10 wfill'}
                            name={'defaultNumberOfPallets'}
                            label={'Default Number Of Pallets'}
                            type={'number'}
                            control={control}
                            defaultValue={initialValues.defaultNumberOfPallets?.toString()}
                        />
                        <ControlledTextField
                            className={'mb10 wfill'}
                            name={'maxPayload'}
                            label={'Max Payload (kg)'}
                            type={'number'}
                            control={control}
                            defaultValue={initialValues.maxPayload?.toString()}
                        />
                        <ControlledTextField
                            className={'mb10 wfill'}
                            name={'weightOfTruck'}
                            label={'Weight Of Truck (kg)'}
                            type={'number'}
                            control={control}
                            defaultValue={initialValues.weightOfTruck?.toString()}
                        />
                        <ControlledTextField
                            className={'mb10 wfill'}
                            name={'tareWeight'}
                            label={'Tare Weight (kg)'}
                            type={'number'}
                            control={control}
                            defaultValue={initialValues.tareWeight?.toString()}
                        />
                        <ControlledTextField
                            className={'mb10 wfill'}
                            name={'percentageWarning'}
                            label={'Percentage Warning'}
                            type={'number'}
                            control={control}
                            defaultValue={initialValues.percentageWarning?.toString()}
                        />
                        <div className={'fdr jcfs mb5'}>
                            <ControlledLabelledCheckBox
                                className={'p0'}
                                name={'isActive'}
                                label={'Is Active'}
                                control={control}
                                defaultValue={initialValues.isActive}
                            />
                        </div>
                        <div className={'fdr flx1 aic jcc mt10'}>
                            <Button
                                className={'cpd mr20'}
                                type={'reset'}
                                onClick={onClose}
                            >
                                CANCEL
                            </Button>
                            <PillButton
                                className={'pl30 pr30'}
                                text={'SAVE'}
                                size={'small'}
                                type={'submit'}
                                color={'secondary'}
                                disabled={!isDirty || isLoading} // Removed !isValid check to allow submission even when validation fails
                            />
                        </div>
                        {/* Display validation errors if any */}
                        {Object.keys(errors).length > 0 && (
                            <div className={'fdc aic mt10 mb10'}>
                                <div style={{ color: '#d32f2f', fontSize: '14px', fontWeight: 'bold', marginBottom: '8px' }}>
                                    Please fix the following validation errors:
                                </div>
                                <div style={{ color: '#d32f2f', fontSize: '12px', textAlign: 'left', maxWidth: '500px', backgroundColor: '#ffebee', padding: '12px', borderRadius: '4px', border: '1px solid #ffcdd2' }}>
                                    {Object.entries(errors).map(([fieldName, error]) => (
                                        <div key={fieldName} style={{ marginBottom: '4px' }}>
                                            <strong>{fieldName.charAt(0).toUpperCase() + fieldName.slice(1)}:</strong> {error?.message}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </form>
        </div>
    );
};

export default TruckTypeForm;
